import React, { useEffect, useState, useMemo } from "react";
import api, { API } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { QuickPublish } from "@/components/QuickPublish";
import { useContentQueue, contentForDay, DayContentMarks, DayContentList } from "@/components/content/CalendarContent";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ChevronLeft, ChevronRight, Send, Trash2, Repeat2, AlertTriangle,
  GripVertical, Sparkles, Loader2, CalendarDays, LayoutGrid, X, Wand2, Eye,
} from "lucide-react";
import PublishPreviewModal from "@/components/PublishPreviewModal";

const STATUS_DOT = {
  QUEUED: "bg-amber-400", PROCESSING: "bg-sky-400", PUBLISHED: "bg-emerald-400",
  AWAITING_PUBLISH: "bg-violet-400", FAILED: "bg-rose-400", EXPIRED_MAX_RECYCLES: "bg-slate-500",
};
const STATUS_STYLE = {
  QUEUED: "bg-amber-500/20 text-amber-400 border-amber-500/40",
  PROCESSING: "bg-sky-500/20 text-sky-400 border-sky-500/40",
  PUBLISHED: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
  AWAITING_PUBLISH: "bg-violet-500/20 text-violet-300 border-violet-500/40",
  FAILED: "bg-rose-500/20 text-rose-400 border-rose-500/40",
  EXPIRED_MAX_RECYCLES: "bg-muted text-muted-foreground border-border",
};
const STATUS_LABEL = { EXPIRED_MAX_RECYCLES: "EXPIRED", AWAITING_PUBLISH: "NEEDS PUBLISH", PROCESSING: "PUBLISHING" };
const FILTERS = ["all", "QUEUED", "AWAITING_PUBLISH", "PUBLISHED", "FAILED", "EXPIRED_MAX_RECYCLES"];
const FILTER_LABEL = { all: "All", QUEUED: "Queued", AWAITING_PUBLISH: "Needs publish", PROCESSING: "Publishing", PUBLISHED: "Published", FAILED: "Failed", EXPIRED_MAX_RECYCLES: "Expired" };

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // index 0=Mon
const TIME_SLOTS = ["09:00", "12:00", "15:00", "18:00"];
const BUCKET_COLOR = {
  "Product Spotlight": "border-indigo-500/50 bg-indigo-500/10 text-indigo-300",
  "Patch Notes/Lore": "border-sky-500/50 bg-sky-500/10 text-sky-300",
  "Behind the Scenes": "border-amber-500/50 bg-amber-500/10 text-amber-300",
  "Promotional Offer": "border-emerald-500/50 bg-emerald-500/10 text-emerald-300",
  "Social Proof/FAQ": "border-rose-500/50 bg-rose-500/10 text-rose-300",
};
const PLATFORMS = ["instagram", "twitter", "facebook", "linkedin"];

export default function CalendarPage() {
  const { active } = useWorkspace();
  const [view, setView] = useState("month");
  const [posts, setPosts] = useState([]);
  const [previewPost, setPreviewPost] = useState(null);
  const [cursor, setCursor] = useState(new Date());
  const [filter, setFilter] = useState("all");
  const [selectedDay, setSelectedDay] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);

  // planner state
  const [templates, setTemplates] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [plannerPlatform, setPlannerPlatform] = useState("instagram");
  const [dragBucket, setDragBucket] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  const [weekRange, setWeekRange] = useState("work"); // "work" (business hours) | "full" (24h)
  const [tz, setTz] = useState(() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { return "UTC"; } });
  const [generating, setGenerating] = useState(false);
  const [library, setLibrary] = useState([]);
  const [applyingPlan, setApplyingPlan] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [publishDueOpen, setPublishDueOpen] = useState(false);
  const contentItems = useContentQueue(active?.id);
  const dueToday = useMemo(() => {
    const end = new Date(); end.setHours(23, 59, 59, 999);
    return posts.filter((p) => ["QUEUED", "AWAITING_PUBLISH"].includes(p.status) && p.scheduled_at && new Date(p.scheduled_at) <= end);
  }, [posts]);

  const load = () => {
    if (!active) return;
    api.get(`/posts?workspace_id=${active.id}`).then((r) => setPosts(r.data)).catch(() => {});
  };
  const loadTemplates = () => {
    if (!active) return;
    api.get(`/schedule-templates?workspace_id=${active.id}`).then((r) => setTemplates(r.data)).catch(() => {});
  };
  useEffect(() => { load(); loadTemplates(); }, [active]); // eslint-disable-line
  useEffect(() => { api.get("/categories").then((r) => setBuckets(r.data.categories)).catch(() => {}); }, []);
  useEffect(() => { api.get("/schedule-templates/library").then((r) => setLibrary(r.data.library)).catch(() => {}); }, []);

  const filtered = useMemo(
    () => posts.filter((p) => filter === "all" || p.status === filter),
    [posts, filter]
  );

  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const startDay = monthStart.getDay();
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const postsForDay = (day) => {
    if (!day) return [];
    return filtered.filter((p) => {
      if (!p.scheduled_at) return false;
      const dt = new Date(p.scheduled_at);
      return dt.getFullYear() === cursor.getFullYear() && dt.getMonth() === cursor.getMonth() && dt.getDate() === day;
    });
  };

  const dayList = selectedDay ? postsForDay(selectedDay) : filtered
    .slice()
    .sort((a, b) => new Date(b.scheduled_at || 0) - new Date(a.scheduled_at || 0));

  // --- Week (time-slot) view helpers (timezone-aware) ---
  const COMMON_TZ = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "Europe/London", "Europe/Paris", "Europe/Berlin", "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore",
    "Asia/Tokyo", "Australia/Sydney", "UTC"];
  if (!COMMON_TZ.includes(tz)) COMMON_TZ.unshift(tz);
  const HOURS = weekRange === "full" ? [...Array(24).keys()] : [8, 10, 12, 14, 16, 18, 20];
  const hourStep = weekRange === "full" ? 1 : 2;
  const weekStart = new Date(cursor);
  weekStart.setDate(cursor.getDate() - cursor.getDay());
  weekStart.setHours(0, 0, 0, 0);
  const weekDays = [...Array(7)].map((_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; });
  const _zparts = (date) => {
    const f = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    const p = {}; for (const x of f.formatToParts(date)) p[x.type] = x.value;
    return { y: +p.year, mo: +p.month - 1, d: +p.day, h: (+p.hour) % 24, mi: +p.minute };
  };
  const _wallToUtc = (y, mo, d, h) => {
    const guess = Date.UTC(y, mo, d, h, 0, 0);
    const f = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    const p = {}; for (const x of f.formatToParts(new Date(guess))) p[x.type] = x.value;
    const asTz = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
    return new Date(guess - (asTz - guess));
  };
  const fmtTz = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: tz });
  const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const postsInCell = (dayDate, hour) => filtered.filter((p) => {
    if (!p.scheduled_at) return false;
    const z = _zparts(new Date(p.scheduled_at));
    const lo = hour === HOURS[0] ? 0 : hour;
    const hi = hour === HOURS[HOURS.length - 1] ? 24 : hour + hourStep;
    return z.y === dayDate.getFullYear() && z.mo === dayDate.getMonth() && z.d === dayDate.getDate() && z.h >= lo && z.h < hi;
  });

  const dispatchNow = async (id) => {
    try {
      const r = await api.post(`/posts/${id}/dispatch-now`);
      if (r.data.post?.status === "PUBLISHED") toast.success("Dispatched to Zernio.");
      else toast.error(`Dispatch: ${r.data.post?.status}. ${r.data.post?.error || ""}`);
      load();
    } catch (e) { toast.error("Dispatch failed."); }
  };
  const remove = async (id) => { await api.delete(`/posts/${id}`); toast.success("Deleted."); load(); };
  const startEdit = (p) => { setEditingId(p.id); setEditText(p.post_text || ""); };
  const cancelEdit = () => { setEditingId(null); setEditText(""); };
  const saveEdit = async (id) => {
    setSavingEdit(true);
    try {
      // preserve paragraph breaks (\n / \n\n) exactly as typed
      const r = await api.patch(`/posts/${id}`, { post_text: editText });
      setPosts((prev) => prev.map((x) => (x.id === id ? { ...x, post_text: r.data.post_text } : x)));
      setEditingId(null); setEditText("");
      toast.success("Saved.");
    } catch (e) { toast.error("Could not save the edit."); }
    setSavingEdit(false);
  };
  const toggleRecycle = async (p) => {
    await api.patch(`/posts/${p.id}`, { recycle_enabled: !p.recycle_enabled });
    load();
  };

  // --- Drag a post to a new day (reschedule) ---
  const onPostDragStart = (e, post) => {
    e.dataTransfer.setData("text/plain", post.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const dropPostOnDay = async (e, day) => {
    e.preventDefault();
    setDragOverDay(null);
    const postId = e.dataTransfer.getData("text/plain");
    if (!postId || !day) return;
    const post = posts.find((p) => p.id === postId);
    if (!post) return;
    const orig = post.scheduled_at ? new Date(post.scheduled_at) : new Date();
    const target = new Date(cursor.getFullYear(), cursor.getMonth(), day, orig.getHours(), orig.getMinutes());
    if (target < new Date()) { toast.error("Can't schedule in the past."); return; }
    try {
      await api.patch(`/posts/${postId}`, { scheduled_at: target.toISOString() });
      toast.success(`Rescheduled to ${target.toLocaleDateString([], { month: "short", day: "numeric" })}`);
      load();
    } catch (err) { toast.error("Could not reschedule."); }
  };

  const dropPostOnCell = async (e, dayDate, hour) => {
    e.preventDefault();
    setDragOverCell(null);
    const postId = e.dataTransfer.getData("text/plain");
    if (!postId) return;
    const target = _wallToUtc(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), hour);
    if (target < new Date()) { toast.error("Can't schedule in the past."); return; }
    try {
      await api.patch(`/posts/${postId}`, { scheduled_at: target.toISOString() });
      toast.success(`Rescheduled to ${target.toLocaleDateString([], { weekday: "short", timeZone: tz })} ${fmtTz(target.toISOString())}`);
      load();
    } catch (err) { toast.error("Could not reschedule."); }
  };

  // --- Weekly planner: drop a bucket into a slot ---
  const templateAt = (weekday, time) => templates.find((t) => t.weekday === weekday && t.time === time);
  const dropBucketOnSlot = async (weekday, time) => {
    setDragOverSlot(null);
    if (!dragBucket) return;
    try {
      await api.post("/schedule-templates", { workspace_id: active.id, weekday, time, bucket: dragBucket, platform: plannerPlatform });
      loadTemplates();
    } catch (e) { toast.error("Could not add slot."); }
    setDragBucket(null);
  };
  const removeTemplate = async (id) => { await api.delete(`/schedule-templates/${id}`); loadTemplates(); };
  const applyPlan = async (plan) => {
    setApplyingPlan(plan.id);
    try {
      const r = await api.post("/schedule-templates/apply-library", { workspace_id: active.id, library_id: plan.id });
      toast.success(`Applied "${r.data.plan}" — ${r.data.applied} slots added.`);
      loadTemplates();
    } catch (e) { toast.error("Could not apply plan."); }
    finally { setApplyingPlan(null); }
  };
  const generateWeek = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/schedule-templates/generate-week", { workspace_id: active.id });
      toast.success(`Generated ${r.data.created} post(s) for the next 7 days.`);
      if (r.data.skipped) toast.message(`${r.data.skipped} skipped (plan limit or error).`);
      setView("month");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate week.");
    } finally { setGenerating(false); }
  };

  if (!active) return null;
  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Content Calendar</h1>
          <p className="text-muted-foreground mt-1">Drag posts between days, or plan recurring buckets by timeslot</p>
        </div>
        <Tabs value={view} onValueChange={setView}>
          <TabsList data-testid="calendar-view-tabs" className="bg-secondary/50">
            <TabsTrigger value="month" data-testid="view-month"><CalendarDays className="h-4 w-4 mr-1.5" /> Month</TabsTrigger>
            <TabsTrigger value="week" data-testid="view-week"><CalendarDays className="h-4 w-4 mr-1.5" /> Week</TabsTrigger>
            <TabsTrigger value="planner" data-testid="view-planner"><LayoutGrid className="h-4 w-4 mr-1.5" /> Weekly planner</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {view === "month" ? (
        <>
          <div className="flex justify-end">
            <Tabs value={filter} onValueChange={(v) => { setFilter(v); setSelectedDay(null); }}>
              <TabsList data-testid="calendar-filter-tabs" className="bg-secondary/50">
                {FILTERS.map((f) => <TabsTrigger key={f} value={f} data-testid={`filter-${f}`}>{FILTER_LABEL[f]}</TabsTrigger>)}
              </TabsList>
            </Tabs>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar grid */}
            <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-lg">{monthLabel}</h3>
                <div className="flex gap-1">
                  <Button data-testid="calendar-prev" variant="ghost" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}><ChevronLeft className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => { setCursor(new Date()); setSelectedDay(null); }}>Today</Button>
                  <Button data-testid="calendar-next" variant="ghost" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5"><GripVertical className="h-3.5 w-3.5" /> Drag a post card onto a day to reschedule it.</p>
              <div className="grid grid-cols-7 gap-1 mb-1">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                  <div key={d} className="text-center text-xs font-mono uppercase text-muted-foreground py-1">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {cells.map((day, i) => {
                  const dayPosts = postsForDay(day);
                  const isSelected = selectedDay === day;
                  const isToday = day && new Date().getDate() === day && new Date().getMonth() === cursor.getMonth() && new Date().getFullYear() === cursor.getFullYear();
                  const isDragOver = dragOverDay === day && day;
                  return (
                    <button
                      key={i}
                      disabled={!day}
                      data-testid={day ? `calendar-day-${day}` : undefined}
                      onClick={() => setSelectedDay(isSelected ? null : day)}
                      onDragOver={(e) => { if (day) { e.preventDefault(); setDragOverDay(day); } }}
                      onDragLeave={() => setDragOverDay((d) => (d === day ? null : d))}
                      onDrop={(e) => dropPostOnDay(e, day)}
                      className={`min-h-[74px] rounded-lg border p-1.5 text-left transition-colors ${
                        !day ? "border-transparent" :
                        isDragOver ? "border-primary bg-primary/20 ring-2 ring-primary/40" :
                        isSelected ? "border-primary bg-primary/10" :
                        "border-border hover:border-primary/40 bg-secondary/20"
                      }`}
                    >
                      {day && (
                        <>
                          <span className={`text-xs font-mono ${isToday ? "text-primary font-bold" : "text-muted-foreground"}`}>{day}</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {dayPosts.slice(0, 4).map((p) => (
                              <span key={p.id} className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[p.status] || "bg-primary"}`} />
                            ))}
                            {dayPosts.length > 4 && <span className="text-[9px] text-muted-foreground">+{dayPosts.length - 4}</span>}
                          </div>
                          <DayContentMarks items={contentForDay(contentItems, cursor, day)} />
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-4 mt-4 text-xs text-muted-foreground">
                {Object.entries(STATUS_DOT).map(([k, v]) => (
                  <span key={k} className="flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${v}`} /> {FILTER_LABEL[k] || k}</span>
                ))}
              </div>
            </div>

            {/* Day / list detail */}
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="font-semibold">
                  {selectedDay ? `${monthLabel.split(" ")[0]} ${selectedDay}` : "All posts"}
                  <span className="text-muted-foreground font-normal"> · {dayList.length}</span>
                </h3>
                {dueToday.length > 0 && (
                  <Button size="sm" variant="outline" data-testid="publish-due-today-btn"
                    onClick={() => setPublishDueOpen(true)}
                    className="h-8 text-xs border-primary/40 text-primary shrink-0">
                    <Send className="h-3 w-3 mr-1" /> Publish due today ({dueToday.length})
                  </Button>
                )}
              </div>
              <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
                {selectedDay && <DayContentList items={contentForDay(contentItems, cursor, selectedDay)} />}
                {dayList.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">No posts.</p>
                ) : dayList.map((p) => (
                  <div key={p.id}
                    draggable={p.status !== "PUBLISHED" && editingId !== p.id}
                    onDragStart={(e) => onPostDragStart(e, p)}
                    className={`rounded-lg border border-border bg-secondary/20 p-3 ${p.status !== "PUBLISHED" ? "cursor-grab active:cursor-grabbing" : ""}`}
                    data-testid={`calendar-post-${p.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-1.5">
                        {p.status !== "PUBLISHED" && <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />}
                        <Badge className={`border text-[10px] ${STATUS_STYLE[p.status]}`}>{STATUS_LABEL[p.status] || p.status}</Badge>
                      </div>
                      <span className="text-[11px] font-mono text-muted-foreground">
                        {p.scheduled_at ? new Date(p.scheduled_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </span>
                    </div>
                    {p.image_path && (
                      <div className="rounded-md overflow-hidden border border-border mb-2 bg-secondary">
                        <img src={`${API}/media/${p.image_path}`} alt="" loading="lazy"
                          className="w-full h-32 object-cover" data-testid={`calendar-post-image-${p.id}`} />
                      </div>
                    )}
                    {editingId === p.id ? (
                      <div className="mb-2" data-testid={`inline-edit-${p.id}`}>
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={Math.min(12, Math.max(4, (editText.split("\n").length + 1)))}
                          autoFocus
                          data-testid={`inline-edit-textarea-${p.id}`}
                          className="w-full rounded-md border border-primary/50 bg-background p-2 text-sm font-sans whitespace-pre-wrap focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        <div className="flex items-center gap-2 mt-1.5">
                          <Button size="sm" onClick={() => saveEdit(p.id)} disabled={savingEdit}
                            data-testid={`inline-edit-save-${p.id}`} className="h-7 text-xs bg-primary hover:bg-primary/90 text-white">
                            {savingEdit ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={savingEdit}
                            data-testid={`inline-edit-cancel-${p.id}`} className="h-7 text-xs text-muted-foreground">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p onClick={() => p.status !== "PUBLISHED" && startEdit(p)}
                        data-testid={`post-text-${p.id}`}
                        title={p.status !== "PUBLISHED" ? "Click to edit" : undefined}
                        className={`text-sm line-clamp-3 mb-2 whitespace-pre-wrap ${p.status !== "PUBLISHED" ? "cursor-text hover:bg-secondary/40 rounded px-0.5 -mx-0.5" : ""}`}>
                        {p.post_text}
                      </p>
                    )}
                    {p.status === "FAILED" && p.error && (
                      <p className="text-xs text-rose-400 flex items-start gap-1 mb-2"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{p.error}</p>
                    )}
                    {p.hashtags?.length > 0 && (
                      <p className="text-[11px] font-mono text-primary/80 mb-2" data-testid={`calendar-post-hashtags-${p.id}`}>
                        {p.hashtags.join(" · ")}
                      </p>
                    )}
                    <QuickPublish platform={p.platform} text={p.post_text} />
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" variant="outline" data-testid={`preview-post-${p.id}`} onClick={() => setPreviewPost(p)} className="h-7 text-xs border-border">
                        <Eye className="h-3 w-3 mr-1" /> Preview
                      </Button>
                      {p.status === "QUEUED" && (
                        <Button size="sm" variant="outline" data-testid={`dispatch-now-${p.id}`} onClick={() => dispatchNow(p.id)} className="h-7 text-xs border-primary/40 text-primary">
                          <Send className="h-3 w-3 mr-1" /> Dispatch now
                        </Button>
                      )}
                      {p.status !== "PUBLISHED" && (
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Repeat2 className="h-3 w-3" />
                          <Switch checked={!!p.recycle_enabled} onCheckedChange={() => toggleRecycle(p)} data-testid={`recycle-toggle-${p.id}`} className="scale-75" />
                        </label>
                      )}
                      <Button size="sm" variant="ghost" data-testid={`delete-post-${p.id}`} onClick={() => remove(p.id)} className="h-7 text-xs text-rose-400 ml-auto">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : view === "week" ? (
        /* -------- Week (time-slot) view -------- */
        <div className="space-y-4" data-testid="week-view">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button data-testid="week-prev" variant="ghost" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - 7))}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm font-medium">{weekDays[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} – {weekDays[6].toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
              <Button data-testid="week-next" variant="ghost" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 7))}><ChevronRight className="h-4 w-4" /></Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex rounded-lg border border-border bg-secondary/40 p-0.5" data-testid="week-range-toggle">
                <button data-testid="week-range-work" onClick={() => setWeekRange("work")}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${weekRange === "work" ? "bg-primary text-white" : "text-muted-foreground"}`}>Business hrs</button>
                <button data-testid="week-range-full" onClick={() => setWeekRange("full")}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${weekRange === "full" ? "bg-primary text-white" : "text-muted-foreground"}`}>24 hours</button>
              </div>
              <select data-testid="week-tz" value={tz} onChange={(e) => setTz(e.target.value)}
                className="text-xs rounded-lg border border-border bg-secondary/40 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary max-w-[160px]">
                {COMMON_TZ.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
              </select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5 -mt-1"><GripVertical className="h-3.5 w-3.5" /> Drag a post onto an exact day &amp; time slot — times shown in {tz.replace(/_/g, " ")}.</p>
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              <div className={`grid grid-cols-[56px_repeat(7,1fr)] gap-1 ${weekRange === "full" ? "max-h-[560px] overflow-y-auto" : ""}`}>
                <div />
                {weekDays.map((d, i) => {
                  const isToday = sameDay(d, new Date());
                  return (
                    <div key={i} className={`text-center py-1.5 rounded-md text-xs font-medium ${isToday ? "bg-primary/15 text-primary" : "text-muted-foreground"}`}>
                      {d.toLocaleDateString(undefined, { weekday: "short" })}<br /><span className="text-sm">{d.getDate()}</span>
                    </div>
                  );
                })}
                {HOURS.map((hour) => (
                  <React.Fragment key={hour}>
                    <div className="text-[11px] text-muted-foreground font-mono pt-2 text-right pr-1">
                      {hour === 0 ? "12a" : hour === 12 ? "12p" : hour > 12 ? `${hour - 12}p` : `${hour}a`}
                    </div>
                    {weekDays.map((d, di) => {
                      const cellKey = `${di}-${hour}`;
                      const cellPosts = postsInCell(d, hour);
                      return (
                        <div key={cellKey}
                          data-testid={`week-cell-${di}-${hour}`}
                          onDragOver={(e) => { e.preventDefault(); setDragOverCell(cellKey); }}
                          onDragLeave={() => setDragOverCell(null)}
                          onDrop={(e) => dropPostOnCell(e, d, hour)}
                          className={`min-h-[${weekRange === "full" ? "40" : "64"}px] rounded-md border p-1 space-y-1 transition-colors ${dragOverCell === cellKey ? "border-primary bg-primary/10" : "border-border bg-card/40"}`}>
                          {cellPosts.map((p) => (
                            <div key={p.id} draggable onDragStart={(e) => onPostDragStart(e, p)}
                              data-testid={`week-post-${p.id}`}
                              className="cursor-grab active:cursor-grabbing rounded bg-secondary/80 border border-border px-1.5 py-1 text-[11px] leading-tight hover:border-primary/50">
                              <span className="font-medium capitalize">{p.platform}</span>
                              <span className="text-muted-foreground"> · {fmtTz(p.scheduled_at)}</span>
                              <p className="line-clamp-2 text-muted-foreground mt-0.5 whitespace-pre-wrap">{p.post_text}</p>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* -------- Weekly planner -------- */
        <div className="space-y-5" data-testid="weekly-planner">
          {/* Starter plans library */}
          <div className="rounded-xl border border-border bg-card p-5" data-testid="planner-library">
            <div className="flex items-center gap-2 mb-1">
              <Wand2 className="h-4 w-4 text-accent" />
              <h3 className="font-semibold">Starter plans</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Drop in a proven weekly plan with one click, then tweak it.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {library.map((plan) => (
                <div key={plan.id} className="rounded-lg border border-border bg-secondary/20 p-4 flex flex-col" data-testid={`planner-plan-${plan.id}`}>
                  <div className="font-medium text-sm">{plan.name}</div>
                  <p className="text-xs text-muted-foreground mt-1 flex-1">{plan.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-[10px] font-mono uppercase text-muted-foreground">{plan.slots.length} slots · {plan.platform}</span>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-primary/40 text-primary"
                      data-testid={`planner-apply-${plan.id}`} disabled={applyingPlan === plan.id} onClick={() => applyPlan(plan)}>
                      {applyingPlan === plan.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Use plan"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bucket palette */}
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
              <div>
                <h3 className="font-semibold">Category buckets</h3>
                <p className="text-xs text-muted-foreground mt-1">Drag a bucket into a slot below to schedule that post type every week.</p>
              </div>
              <div className="flex items-center gap-3">
                <Select value={plannerPlatform} onValueChange={setPlannerPlatform}>
                  <SelectTrigger className="w-36" data-testid="planner-platform-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="planner-generate-button" onClick={generateWeek} disabled={generating || templates.length === 0}
                  className="bg-primary hover:bg-primary/90 text-white">
                  {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Fill next 7 days
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {buckets.map((b) => (
                <div key={b} draggable onDragStart={() => setDragBucket(b)} onDragEnd={() => setDragBucket(null)}
                  data-testid={`bucket-chip-${b}`}
                  className={`px-3 py-2 rounded-lg border text-xs font-medium cursor-grab active:cursor-grabbing flex items-center gap-2 ${BUCKET_COLOR[b] || "border-border bg-secondary/40 text-foreground"}`}>
                  <GripVertical className="h-3.5 w-3.5 opacity-60" /> {b}
                </div>
              ))}
            </div>
          </div>

          {/* Weekly grid */}
          <div className="rounded-xl border border-border bg-card p-5 overflow-x-auto">
            <div className="min-w-[720px]">
              <div className="grid grid-cols-8 gap-2 mb-2">
                <div className="text-xs font-mono uppercase text-muted-foreground" />
                {WEEKDAYS.map((d) => (
                  <div key={d} className="text-center text-xs font-mono uppercase text-muted-foreground">{d}</div>
                ))}
              </div>
              {TIME_SLOTS.map((time) => (
                <div key={time} className="grid grid-cols-8 gap-2 mb-2">
                  <div className="flex items-center text-xs font-mono text-muted-foreground">{time}</div>
                  {WEEKDAYS.map((_, wd) => {
                    const tpl = templateAt(wd, time);
                    const over = dragOverSlot === `${wd}-${time}`;
                    return (
                      <div key={wd}
                        data-testid={`planner-slot-${wd}-${time}`}
                        onDragOver={(e) => { e.preventDefault(); setDragOverSlot(`${wd}-${time}`); }}
                        onDragLeave={() => setDragOverSlot((s) => (s === `${wd}-${time}` ? null : s))}
                        onDrop={() => dropBucketOnSlot(wd, time)}
                        className={`min-h-[56px] rounded-lg border transition-colors flex items-center justify-center p-1 ${
                          over ? "border-primary bg-primary/20 ring-2 ring-primary/40" :
                          tpl ? BUCKET_COLOR[tpl.bucket] || "border-border" :
                          "border-dashed border-border/70 bg-secondary/10 hover:border-primary/40"
                        }`}>
                        {tpl ? (
                          <div className="relative w-full text-center">
                            <span className="text-[10px] font-medium leading-tight block px-1">{tpl.bucket}</span>
                            <span className="text-[9px] text-muted-foreground capitalize">{tpl.platform}</span>
                            <button data-testid={`planner-remove-${wd}-${time}`} onClick={() => removeTemplate(tpl.id)}
                              className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-background border border-border flex items-center justify-center hover:bg-rose-500/20">
                              <X className="h-2.5 w-2.5" />
                            </button>
                          </div>
                        ) : <span className="text-[10px] text-muted-foreground/40">drop</span>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            "Fill next 7 days" generates a queued, anti-slop post for each slot on its next weekday. Recycling is on by default.
          </p>
        </div>
      )}

      <Dialog open={publishDueOpen} onOpenChange={setPublishDueOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="publish-due-dialog">
          <DialogHeader>
            <DialogTitle>Publish everything due today</DialogTitle>
            <DialogDescription>
              {dueToday.length} post{dueToday.length === 1 ? "" : "s"} ready. Copy the caption and open each platform — you're already logged in there. Posts on auto-publish will also go out on their own.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {dueToday.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Nothing due today.</p>
            ) : dueToday.map((p) => (
              <div key={p.id} className="rounded-lg border border-border bg-secondary/20 p-3" data-testid={`due-post-${p.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <Badge className={`border text-[10px] ${STATUS_STYLE[p.status]}`}>{STATUS_LABEL[p.status] || p.status}</Badge>
                  <span className="text-[11px] font-mono text-muted-foreground">
                    {p.scheduled_at ? new Date(p.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                  </span>
                </div>
                <p className="text-sm line-clamp-3 mb-2 whitespace-pre-wrap">{p.post_text}</p>
                <QuickPublish platform={p.platform} text={p.post_text} />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <PublishPreviewModal
        open={!!previewPost}
        onOpenChange={(o) => { if (!o) setPreviewPost(null); }}
        post={previewPost}
      />
    </div>
  );
}
